import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { AdminTeamDTO } from "@shaarp/common";
import { useState } from "react";
import { ButtonLoading } from "../button-loading";
import { DialogFooter, DialogHeader } from "../ui/dialog";
import { Input } from "../ui/input";

export const DialogAdminTeamBilling = ({
  team,
  open,
  onClose,
}: {
  team: AdminTeamDTO;
  open: boolean;
  onClose: () => void;
}) => {
  const toastError = useToastError();
  const { toast } = useToast();
  const api = useApi();
  const { t } = useTranslation();

  const [enabled, setEnabled] = useState<boolean>(team.billingEnabled);
  const [paymentMethodRequired, setPaymentMethodRequired] = useState<boolean>(
    team.paymentMethodRequired
  );
  const [email, setEmail] = useState<string>(team.billingEmail);
  const [monthlyDiscount, setMonthlyDiscount] = useState<string>(
    team.monthlyDiscount || ""
  );
  const [annualDiscount, setAnnualDiscount] = useState<string>(
    team.annualDiscount || ""
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const save = async () => {
    try {
      setIsLoading(true);
      await api.post(`/admin/team/${team.id}/billing`, {
        billing: {
          enabled,
          email,
        },
        paymentMethodRequired,
        monthlyDiscount,
        annualDiscount,
      });
      toast({
        variant: "default",
        title: t("admin.dataTableAdminTeams.dialogs.billing.successTitle"),
        description: t(
          "admin.dataTableAdminTeams.dialogs.billing.successDescription"
        ),
      });
    } catch (e) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>
            {t("admin.dataTableAdminTeams.dialogs.billing.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="py-3 space-y-5 border-t">
          <div className="col-span-1">
            <div className="font-bold">
              {t("admin.dataTableAdminTeams.dialogs.billing.enableBilling")}
            </div>
            <div>
              <Switch checked={enabled} onCheckedChange={setEnabled} />
            </div>
          </div>
          <div className="col-span-1">
            <div className="font-bold">
              {t("admin.dataTableAdminTeams.dialogs.billing.billingEmail")}
            </div>
            <div>
              <Input
                value={team.billingEmail}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          <div className="col-span-1">
            <div className="font-bold">
              {t(
                "admin.dataTableAdminTeams.dialogs.billing.paymentMethodRequired"
              )}
            </div>
            <div>
              <Switch
                checked={paymentMethodRequired}
                onCheckedChange={setPaymentMethodRequired}
              />
            </div>
          </div>
          <div className="col-span-1">
            <div className="font-bold">
              {t("admin.dataTableAdminTeams.dialogs.billing.monthlyDiscount")}
            </div>
            <div>
              <Input
                value={monthlyDiscount}
                onChange={(e) => setMonthlyDiscount(e.target.value)}
              />
            </div>
          </div>
          <div className="col-span-1">
            <div className="font-bold">
              {t("admin.dataTableAdminTeams.dialogs.billing.annualDiscount")}
            </div>
            <div>
              <Input
                value={annualDiscount}
                onChange={(e) => setAnnualDiscount(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <ButtonLoading onClick={save} isLoading={isLoading}>
            {t("admin.dataTableAdminTeams.dialogs.billing.save")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
